import React, { useState } from "react";

import UsageToolbar from "@/components/usage/UsageToolbar";
import UsageSummary from "@/components/usage/UsageSummary";
import UsageByUserTable from "@/components/usage/UsageByUserTable";
import { readEngineeringDetailPreference, writeEngineeringDetailPreference } from "@/components/usage/engineeringDetailPreference";
import { useTenantUsage } from "@/components/usage/useTenantUsage";
import UsageSkeleton from "@/components/usage/UsageSkeleton";

/**
 * One tenant's usage, for the SuperAdmin tenant-details page.
 *
 * The fetching lives in `useTenantUsage`, shared with the SuperAdmin usage
 * page: the summary and the per-user breakdown arrive in a single call so the
 * two cannot straddle a write and disagree, the search is server-side, and the
 * two busy flags keep a keystroke from greying out the date controls. All of
 * that used to be written out here, and drifted from the other screen.
 *
 * This tab stays deliberately plain — it is one tab among many on a tenant
 * page. The fleet comparison, the ranked tenants and the share-of-fleet context
 * belong to `/super-admin/usage`, which owns the cross-tenant question.
 */
const UsagePanel = ({ tenantId }) => {
  const [range, setRange] = useState({ from: "", to: "" });
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState(readEngineeringDetailPreference);

  const handleDetailChange = (next) => {
    writeEngineeringDetailPreference(next);
    setDetail(next);
  };

  const { data, loading, searchLoading } = useTenantUsage({ tenantId, range, detail, search });

  return (
    <div className="flex flex-col gap-4">
      <UsageToolbar range={range} onRangeChange={setRange} disabled={loading} detail={detail} onDetailChange={handleDetailChange} />

      {loading && !data ? (
        <div className="flex flex-col gap-4">
          <UsageSkeleton className="h-64 w-full" />
          <UsageSkeleton className="h-40 w-full" />
        </div>
      ) : (
        <>
          <UsageSummary summary={data?.summary} />
          <UsageByUserTable data={data?.byUser} search={search} onSearchChange={setSearch} loading={searchLoading} />
        </>
      )}
    </div>
  );
};

export default UsagePanel;
